import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const RECOVERABLE_ERRORS = ['invalid_grant', 'invalid state', 'Invalid state'];

const isOrgMembershipError = (err: Error) =>
  err.message?.includes('organization membership');

const isRecoverableError = (err: Error) =>
  RECOVERABLE_ERRORS.some(e => err.message?.includes(e));

const isCallbackUrl = () => {
  const p = new URLSearchParams(window.location.search);
  // A successful callback has 'code'; an error callback has 'error' — treat
  // error callbacks as non-recoverable so the error UI is shown.
  return (p.has('code') || p.has('state')) && !p.has('error');
};

const Spinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
  </div>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0();

  React.useEffect(() => {
    const isCallback = isCallbackUrl();

    if (error && isRecoverableError(error)) {
      if (isCallback) {
        const tryReload = () => {
          if (Object.keys(localStorage).some(k => k.startsWith('@@auth0spajs@@'))) {
            window.history.replaceState({}, document.title, window.location.pathname);
            window.location.reload();
          } else {
            const interval = setInterval(() => {
              if (Object.keys(localStorage).some(k => k.startsWith('@@auth0spajs@@'))) {
                clearInterval(interval);
                window.history.replaceState({}, document.title, window.location.pathname);
                window.location.reload();
              }
            }, 100);
          }
        };
        tryReload();
      } else {
        localStorage.removeItem('@@auth0spajs@@::' + process.env.REACT_APP_AUTH0_CLIENT_ID);
        loginWithRedirect({ appState: { returnTo: window.location.pathname } });
      }
      return;
    }

    if (!isLoading && !isAuthenticated && !error && !isCallback) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, error]);

  if (isLoading || !isAuthenticated) {
    if (error && !isRecoverableError(error) && !isCallbackUrl()) {
      if (isOrgMembershipError(error)) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
            <div className="bg-white p-10 rounded-2xl shadow-2xl max-w-md w-full mx-4 text-center">
              <div className="mb-6">
                <div className="text-5xl mb-4">⚠️</div>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Cuenta incorrecta</h1>
                <p className="text-gray-600">
                  La cuenta que usaste no pertenece a ninguna organización en Atiende.
                  Por favor, inicia sesión con la cuenta correcta.
                </p>
              </div>
              <button
                onClick={() => loginWithRedirect({ authorizationParams: { prompt: 'select_account' } })}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                Iniciar sesión con otra cuenta
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-600">Authentication Error: {error.message}</p>
            <button
              onClick={() => loginWithRedirect()}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
    return <Spinner />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
